// GA4 event layer (TD-146).
//
// Until now the site pushed nothing to the dataLayer beyond the market/page_type
// seed, so GA4 had no custom events at all — every conversion existed on the
// Meta side only. These helpers are the GA4 half.
//
// Naming rule from the spec: the event name describes the action, never the
// page. Page identity travels in `page_type`, market in `market`. Adding a
// landing page must not require adding an event.

import { getEventContext } from "@/utils/eventContext";

/**
 * Push a GA4 event onto the dataLayer, stamped with market, page_type and a
 * canonicalised source_page. Caller params win, so a call site can override.
 */
export const trackGa4Event = (
    eventName: string,
    eventParams?: Record<string, any>
) => {
    if (typeof window === "undefined") return;

    const layer = ((window as any).dataLayer = (window as any).dataLayer || []);

    layer.push({
        event: eventName,
        ...getEventContext(),
        ...(eventParams ?? {})
    });
};

// --- Key events (marked as conversions in GA4) ---------------------------

/**
 * Lead Qualification — an enquiry we can follow up. Meta equivalent: Lead.
 */
export const trackGenerateLead = (params?: Record<string, any>) => {
    trackGa4Event("generate_lead", params);
};

/**
 * Trial Arranged — a trial slot actually booked. Meta equivalent: Schedule.
 */
export const trackTrialBooked = (params?: Record<string, any>) => {
    trackGa4Event("trial_booked", params);
};

// Trial Done (`trial_completed`) and Enrolment (`purchase`) are deliberately
// absent: both are offline outcomes that cannot be observed from the browser.
// They arrive via the CRM offline conversion upload in TD-152.

// --- Diagnostics (NOT key events) ---------------------------------------
//
// These measure where people drop off. Marking them as conversions is the
// mistake TD-154 unwound with WhatsApp widget opens, and it is what makes a
// reported conversion count meaningless.

export const trackFormStart = (formName: string) => {
    trackGa4Event("form_start", { form_name: formName });
};

/**
 * One event with a `step` parameter rather than one event per step, so a form
 * gaining a step does not mean a new event name and a new GA4 registration.
 */
export const trackFormStep = (formName: string, step: string) => {
    trackGa4Event("form_step", { form_name: formName, step });
};
